import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";

// Badges des signaux d'intégrité de la capture (vague 2) pour les fiches POI et
// soumission. Chaque signal : vert si OK, rouge si bloquant échoué, orange si
// signal négatif non bloquant. Le motif est affiché en clair.

const LIBELLE: Record<string, string> = {
  positionAppareil: "Position appareil",
  positionMarqueur: "Position marqueur",
  precision: "Précision GPS",
  distanceMarqueur: "Distance marqueur",
  positionSimulee: "Position simulée",
  attestation: "Attestation appareil",
  vitesse: "Vitesse",
  simPays: "Pays SIM",
  multiCompte: "Multi-comptes",
};

function detail(rule: string, s: any): string {
  switch (rule) {
    case "precision": return s.accuracyM != null ? `${s.accuracyM} m (seuil ${s.seuilM})` : "";
    case "distanceMarqueur": return s.distanceM != null ? `${s.distanceM} m (seuil ${s.seuilM})` : "";
    case "vitesse": return s.kmh != null ? `${s.kmh} km/h (seuil ${s.seuil})` : "";
    case "simPays": return `${s.sim ?? "?"} → ${s.cible ?? "?"}`;
    case "multiCompte": return `${s.nbComptes ?? "?"} comptes`;
    case "positionSimulee": return s.simulee ? "simulée" : "réelle";
    case "attestation": return [s.environnement, s.motif].filter(Boolean).join(" · ");
    default: return "";
  }
}

function couleur(s: any): { bg: string; fg: string } {
  if (s?.ok) return { bg: "rgba(53,106,15,0.15)", fg: "#356A0F" };           // vert
  if (String(s?.mode).toLowerCase() === "bloquant") return { bg: "rgba(162,58,13,0.15)", fg: "#A23A0D" }; // rouge
  return { bg: "rgba(124,86,0,0.18)", fg: "#7C5600" };                       // orange (signal)
}

export default function IntegriteBadges({ signaux, exif }: { signaux?: Record<string, any> | null; exif?: any }) {
  const entries = signaux ? Object.entries(signaux) : [];
  const aExif = exif && exif.present;
  if (entries.length === 0 && !aExif) {
    return <Typography sx={{ fontSize: 12, color: "var(--c-text-muted, #999)" }}>Aucun signal d'intégrité.</Typography>;
  }
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
      {entries.map(([rule, s]) => {
        const c = couleur(s);
        const d = detail(rule, s);
        const label = (LIBELLE[rule] ?? rule) + (d ? ` : ${d}` : "");
        const motif = !s?.ok ? `${(s?.mode ?? "").toString()}${s?.motif ? " — " + s.motif : ""}` : "conforme";
        return (
          <Tooltip key={rule} title={motif}>
            <Chip size="small" label={label}
              sx={{ background: c.bg, color: c.fg, fontWeight: 600, fontSize: 11 }} />
          </Tooltip>
        );
      })}
      {aExif && (
        <Tooltip title={`EXIF — date ${exif.dateConforme ? "OK" : "écart"} · GPS ${exif.gpsConforme ? "OK" : "écart"}`}>
          <Chip size="small" label={`EXIF ${exif.gpsConforme && exif.dateConforme ? "OK" : "à vérifier"}`}
            sx={{
              background: exif.gpsConforme && exif.dateConforme ? "rgba(53,106,15,0.15)" : "rgba(124,86,0,0.18)",
              color: exif.gpsConforme && exif.dateConforme ? "#356A0F" : "#7C5600", fontWeight: 600, fontSize: 11,
            }} />
        </Tooltip>
      )}
    </Box>
  );
}
